import { ORAL_MEDICATION_NAMES } from "@/types/enums/weight-loss-medications";
import { DoseShipmentAllocation, Medication } from "@/types/medication";

import { DoseComponentFormValues } from "./dose-form-schema";
import {
  doseAllocationAdministrationCounts,
  dosePlanStocks,
  doseStockAdministrationQuotas,
  getDoseStockCoverage,
  proportionalDosePlan,
} from "./dose-stock-planning";
import {
  DoseInventoryPreview,
  ShipmentLot,
} from "@/query/medications/medications";

export interface DoseLotInventory {
  selectedMedication?: Medication;
  shipments: Array<
    Pick<ShipmentLot, "id" | "lotNumber"> & Partial<ShipmentLot>
  >;
  locationId?: string | null;
  providerId?: string | null;
  compoundMedicationId?: string | null;
  preview?: DoseInventoryPreview;
  previewError?: boolean;
  isCheckingInventory?: boolean;
}
export const roundDoseQuantity = (quantity: number) =>
  Math.round(quantity * 1e6) / 1e6;

/** Scope and inventory snapshot, not merely a blanket extra-yield permission. */
export function doseAllocationDecisionKey(
  component: DoseComponentFormValues,
  inventory: DoseLotInventory,
): string {
  return JSON.stringify([
    component.medicationId,
    component.id,
    component.amount,
    component.unit,
    component.quantity,
    component.dilutionId ?? null,
    Object.entries(
      doseStockAdministrationQuotas(component, inventory) ?? {},
    ).sort(([a], [b]) => a.localeCompare(b)),
    inventory.compoundMedicationId ?? null,
    component.correctActualInventoryUse ?? false,
    inventory.providerId ?? null,
    inventory.locationId ?? null,
    inventory.preview,
    inventory.shipments
      .map((lot) => [
        lot.id,
        lot.remainingQuantity,
        lot.providerAvailableQuantity,
        lot.extraYieldEligible,
      ])
      .sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
  ]);
}

export function doseLotCapacity(
  lot: DoseLotInventory["shipments"][number],
  inventory: DoseLotInventory,
) {
  const owned =
    inventory.preview?.ownedLots.find((entry) => entry.shipmentId === lot.id)
      ?.outstanding ?? 0;
  const wrongSite =
    !!inventory.locationId &&
    !!lot.locationId &&
    lot.locationId !== inventory.locationId;
  const eligible = lot.extraYieldEligible !== false && !wrongSite;
  const available = eligible
    ? Math.max(0, lot.providerAvailableQuantity ?? lot.remainingQuantity ?? 0)
    : 0;
  const reservationRestricted =
    lot.providerAvailableQuantity != null &&
    lot.providerAvailableQuantity < (lot.remainingQuantity ?? 0);
  return {
    capacity: available + owned,
    allowExtraYield:
      lot.extraYieldEligible === true && !wrongSite && !reservationRestricted,
  };
}

/** One shared guard for form presentation, both submit boundaries and both payload adapters. */
export function doseAllocationError(
  component: DoseComponentFormValues,
  inventory: DoseLotInventory,
): string | undefined {
  const staged = proportionalDosePlan(component, inventory);
  if (staged) component = { ...component, ...staged };
  if (ORAL_MEDICATION_NAMES.includes(component.name)) return undefined;
  if (component.lotSelection && component.lotSelection.type !== "lot")
    return "Choose lots and assign the amount used from each lot.";
  if (component.clinicalShipmentIds !== undefined) {
    if (
      !inventory.preview?.allowsClinicalLotReferences ||
      inventory.previewError ||
      inventory.isCheckingInventory
    )
      return "Clinical lot references could not be verified. Refresh and try again.";
    if (
      component.lotAllocations !== undefined ||
      component.checkoutReuse ||
      component.correctActualInventoryUse
    )
      return "Clinical lot references cannot be combined with an inventory allocation.";
    if (
      new Set(component.clinicalShipmentIds).size !==
        component.clinicalShipmentIds.length ||
      component.clinicalShipmentIds.some(
        (id) => !inventory.shipments.some((lot) => lot.id === id),
      )
    )
      return "A selected lot reference is no longer available. Choose lots again.";
  }
  const currentCodes = (component.lotSelection?.value ?? "")
    .split("\n")
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  const baselineCodes =
    component.inventoryBaseline?.medicationId === component.medicationId
      ? (component.inventoryBaseline?.untrackedLots ?? []).map((value) =>
          value.trim().toLowerCase(),
        )
      : [];
  if (
    currentCodes.some(
      (code) =>
        !baselineCodes.includes(code) &&
        inventory.shipments.some(
          (lot) => lot.lotNumber?.trim().toLowerCase() === code,
        ),
    )
  )
    return inventory.preview?.allowsClinicalLotReferences
      ? "Choose the matching inventory lot reference."
      : "Choose the tracked lots and assign their amounts.";
  const allocations = component.lotAllocations;
  if (allocations === undefined) {
    if (component.stockAdministrationQuotas !== undefined)
      return "Choose inventory lots before saving.";
    const baseline = component.inventoryBaseline;
    if (
      baselineCodes.length > 0 &&
      currentCodes.length === 0 &&
      inventory.preview?.ordinaryEditMovesInventory === true &&
      inventory.preview.ownedLots.some((owned) => owned.outstanding > 0)
    ) {
      if (
        getDoseStockCoverage(
          inventory.preview.ownedLots.map((owned) => ({
            shipmentId: owned.shipmentId,
            stockId: owned.stockId,
            quantity: owned.outstanding,
          })),
          inventory.preview.stocks,
          component.quantity,
        ).some((coverage) => coverage.deltaPhysical !== 0)
      )
        return "Keep a custom lot or assign the full dose quantity to inventory lots before removing the last custom lot.";
    }
    const changedPhysicalInputs =
      !!baseline &&
      (baseline.medicationId !== component.medicationId ||
        baseline.amount !== component.amount ||
        baseline.quantity !== component.quantity ||
        baseline.unit !== component.unit ||
        (baseline.dilutionId ?? null) !== (component.dilutionId ?? null));
    if (
      component.id &&
      inventory.preview?.ordinaryEditMovesInventory !== false
    ) {
      if (
        inventory.preview?.stocks.some(
          (stock) =>
            roundDoseQuantity(stock.target) !==
            roundDoseQuantity(stock.originalTarget),
        ) &&
        (inventory.preview.ownedLots.length > 0 ||
          baseline?.stockId ||
          baseline?.hasTrackedInventory)
      )
        return "Choose lots in Edit dose and assign the new required amount before saving.";
      if (
        changedPhysicalInputs &&
        (baseline?.stockId || baseline?.hasTrackedInventory) &&
        (!inventory.preview ||
          inventory.previewError ||
          !inventory.preview.stocks.length)
      )
        return "Inventory requirements could not be verified. Open Edit dose to review its lots.";
    }
    return undefined; // Untouched ordinary/default or genuinely untracked history.
  }
  if (inventory.isCheckingInventory)
    return "Wait for inventory to finish checking.";
  if (!inventory.preview || inventory.previewError)
    return "Inventory requirements could not be verified. Refresh and try again.";
  const typedLots = (component.lotSelection?.value ?? "")
    .split("\n")
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  // Custom-only history has no physical draw (including no checkout top-up),
  // but an explicit quota must still agree with its empty physical plan.
  if (
    !component.correctActualInventoryUse &&
    !component.checkoutReuse &&
    !allocations.length &&
    typedLots.length > 0 &&
    typedLots.every(
      (code) =>
        !inventory.shipments.some(
          (lot) => lot.lotNumber?.trim().toLowerCase() === code,
        ),
    )
  ) {
    const quotas = doseStockAdministrationQuotas(component, inventory);
    if (!quotas || Object.values(quotas).some((quota) => quota !== 0))
      return "Assign exactly the planned physical amount across the selected lots.";
    return undefined;
  }
  if (
    !component.checkoutReuse &&
    (!inventory.preview.drawsAtDose ||
      inventory.preview.ordinaryEditMovesInventory === false) &&
    !(
      component.id &&
      component.correctActualInventoryUse &&
      inventory.preview.ordinaryEditMovesInventory === false
    )
  )
    return "Inventory is recorded at checkout. Keep the recorded inventory or explicitly correct an existing sale's actual use.";
  if (
    component.correctActualInventoryUse &&
    (!component.id ||
      inventory.preview.ordinaryEditMovesInventory !== false ||
      component.checkoutReuse)
  )
    return "Actual-use corrections require an existing checkout-linked dose.";
  const stocks = inventory.preview.stocks;
  if (!stocks.length)
    return "This medication has no tracked inventory mapping.";
  const seen = new Set<string>();
  const key = doseAllocationDecisionKey(component, inventory);
  for (const allocation of allocations) {
    if (seen.has(allocation.shipmentId)) return "Select each lot only once.";
    seen.add(allocation.shipmentId);
    const lot = inventory.shipments.find(
      (candidate) => candidate.id === allocation.shipmentId,
    );
    const stock = stocks.find(
      (candidate) => candidate.stockId === allocation.stockId,
    );
    if (!lot || lot.stockId !== allocation.stockId || !stock)
      return "A selected lot is no longer available. Choose lots again.";
    if (
      component.id &&
      !inventory.preview.ordinaryEditMovesInventory &&
      !inventory.preview.ownedLots.some(
        (owned) => owned.shipmentId === allocation.shipmentId,
      )
    )
      return "Correct actual inventory use only on this dose's originally recorded lots.";
    if (
      !stock.unit ||
      !Number.isFinite(allocation.quantity) ||
      allocation.quantity <= 0 ||
      !Number.isFinite(stock.perAdministration) ||
      stock.perAdministration <= 0 ||
      !Number.isFinite(allocation.quantity / stock.perAdministration) ||
      allocation.quantity / stock.perAdministration <= 0
    )
      return "Enter a valid amount for every lot.";
    if (component.checkoutReuse) {
      if (
        component.id ||
        lot.chargedTodayQuantity == null ||
        allocation.allowExtraYield ||
        allocations.filter(
          (candidate) => candidate.stockId === allocation.stockId,
        ).length !== 1
      )
        return "The selected checkout charge is no longer available. Choose lots again.";
      continue; // Immutable checkout snapshot reuse is not a new physical draw.
    }
    const capacity = doseLotCapacity(lot, inventory);
    const allocationKey = JSON.stringify([key, allocation.quantity]);
    if (
      roundDoseQuantity(allocation.quantity - capacity.capacity) > 0 &&
      (!capacity.allowExtraYield ||
        !allocation.allowExtraYield ||
        allocation.decisionKey !== allocationKey)
    )
      return "Review the selected lot amounts before saving.";
    if (allocation.allowExtraYield && allocation.decisionKey !== allocationKey)
      return "Inventory changed. Choose lots again to review extra yield.";
  }
  const quotas = doseStockAdministrationQuotas(component, inventory);
  if (!quotas) return "Choose inventory lots and assign their amounts.";
  if (
    Object.entries(quotas).some(
      ([stockId, quota]) =>
        !Number.isFinite(quota) ||
        quota < 0 ||
        (quota > 0 && !stocks.some((stock) => stock.stockId === stockId)),
    )
  )
    return "Review the amounts assigned to the selected lots.";
  // Checkout reuse identifies an immutable charged draw, not a fresh physical plan.
  if (!component.checkoutReuse) {
    const coverage = getDoseStockCoverage(
      allocations,
      dosePlanStocks(component, inventory),
      component.quantity,
    );
    if (
      coverage.some(
        (entry) =>
          !Number.isFinite(entry.deltaPhysical) ||
          entry.deltaPhysical > 0 ||
          (typedLots.length === 0 && entry.deltaPhysical !== 0),
      )
    )
      return typedLots.length
        ? "The selected inventory lots exceed a required stock amount."
        : "Cover every required stock exactly with the selected lots.";
  }
  const counts = doseAllocationAdministrationCounts(
    component,
    inventory,
    quotas,
  );
  if (!counts)
    return "Assign exactly the planned physical amount across the selected lots.";
  return undefined;
}

export function assertDoseLotResolution(
  component: DoseComponentFormValues,
  inventory: DoseLotInventory,
) {
  const error = doseAllocationError(component, inventory);
  if (error) throw new Error(error);
}

/** Physical base units -> administration counts at full precision. Never round this quotient. */
export function serializeDoseAllocations(
  component: DoseComponentFormValues,
  inventory: DoseLotInventory,
): DoseShipmentAllocation[] | undefined {
  const staged = proportionalDosePlan(component, inventory);
  if (staged) component = { ...component, ...staged };
  assertDoseLotResolution(component, inventory);
  if (
    ORAL_MEDICATION_NAMES.includes(component.name) ||
    component.lotAllocations === undefined
  )
    return undefined;
  if (!component.lotAllocations.length) return [];
  const counts = doseAllocationAdministrationCounts(
    component,
    inventory,
    doseStockAdministrationQuotas(component, inventory)!,
  )!;
  return component.lotAllocations.map((allocation, index) => ({
    shipmentId: allocation.shipmentId,
    quantity: counts[index],
    ...(!component.checkoutReuse && {
      expectedStockId: allocation.stockId,
      expectedStockQuantity: allocation.quantity,
      expectedStockUnit: inventory.preview!.stocks.find(
        (stock) => stock.stockId === allocation.stockId,
      )!.unit,
    }),
    ...(allocation.allowExtraYield && { allowExtraYield: true }),
  }));
}
